import { type OrgScope, withOrg, withTransactionalSql } from '@aimfora/db'
import {
  approveCleanGrades,
  createQuestion,
  getGradeDetail,
  getGradingOverview,
  isDomainError,
  loadResolvedPlan,
  peekCredits,
  reopenGrade,
  saveTeacherGrade,
  startGradingRun,
} from '@aimfora/db/repositories'
import { notFound } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import {
  approveCleanSchema,
  askAiSchema,
  examIdSchema,
  explainTaskSchema,
  saveTeacherGradeSchema,
  startGradingRunSchema,
  submissionIdSchema,
} from '~/lib/domain'
import type { StartGradingResult } from '~/lib/views'
import { requireOrg } from '~/middleware/auth'
import { limitExpensive, limitRuns } from '~/middleware/rate-limit'
import { getAiStatus } from '~/server/ai.server'
import { getBudgetDefaults } from '~/server/billing.server'
import { getDb } from '~/server/db.server'
import { enqueueGradingRun, enqueueQuestion } from '~/server/jobs.server'
import { RequestRejected } from '~/server/rejected.server'
import {
  toGradeListView,
  toGradeResultView,
  toGradingOverviewView,
  toPrintView,
  toQuestionView,
} from '~/server/views.server'

/**
 * Grading: starting a run, following it, and reviewing, editing and approving results. Every
 * function runs behind `requireOrg` and ends up in a repository that checks the exam's owner or an
 * organisation admin; anyone else gets the same "not found" as for something that does not exist.
 * Totals and grades are computed on the server again whatever the browser sends.
 */

/** A missing exam or submission, and one the caller may not see, are a plain "not found". */
async function visible<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise
  } catch (error) {
    if (isDomainError(error) && error.code === 'not_found') throw notFound()
    throw error
  }
}

export const getGradingOverviewFn = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    const overview = await visible(getGradingOverview(context.scope, data.examId))
    const credits = await peekCredits(context.scope)
    const plan = await loadResolvedPlan(context.scope)
    return toGradingOverviewView(context.scope, overview, await getAiStatus(), {
      available: credits.available,
      reserved: credits.reserved,
      unlimited: plan.entitlements.unlimitedCredits,
    })
  })

/** What the organisation can grade with now, for the counter in the top bar. */
export const getCreditsFn = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .handler(async ({ context }) => {
    const credits = await peekCredits(context.scope)
    return { available: credits.available }
  })

export const startGradingRunFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg, limitRuns])
  .validator(startGradingRunSchema)
  .handler(async ({ context, data }): Promise<StartGradingResult> => {
    const ai = await getAiStatus()
    if (!ai.configured || !ai.provider || !ai.model) throw new RequestRejected('ai_not_configured')
    const { ctx } = context.scope
    const result = await visible(
      // The run, its jobs and the queued fan-out job are one transaction on one connection
      withTransactionalSql(getDb().pool, (tx, queue) =>
        startGradingRun(withOrg(tx, ctx), {
          examId: data.examId,
          submissionIds: data.submissionIds,
          ...(data.doubleGrading === undefined ? {} : { doubleGrading: data.doubleGrading }),
          provider: ai.provider as string,
          model: ai.model as string,
          budgetDefaults: getBudgetDefaults(),
          enqueue: (payload) => enqueueGradingRun(payload, queue),
        }),
      ),
    )
    return { runId: result.runId, queued: result.queued.length, skipped: result.skipped }
  })

export const getGradeResultFn = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(submissionIdSchema)
  .handler(async ({ context, data }) => {
    const view = toGradeResultView(
      context.scope,
      await visible(getGradeDetail(context.scope, data.submissionId)),
    )
    // Nothing to review before the AI has proposed a grade
    if (!view) throw notFound()
    return view
  })

export const saveTeacherGradeFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(saveTeacherGradeSchema)
  .handler(async ({ context, data }) => {
    await visible(saveTeacherGrade(context.scope, data))
  })

/** Approve what the teacher chose to approve unopened; each result is checked again on the server. */
export const approveCleanGradesFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(approveCleanSchema)
  .handler(async ({ context, data }) =>
    visible(approveCleanGrades(context.scope, data.examId, data.submissionIds)),
  )

export const reopenGradeFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(submissionIdSchema)
  .handler(async ({ context, data }) => {
    await visible(reopenGrade(context.scope, data.submissionId))
  })

async function queueQuestion(
  context: { scope: OrgScope },
  input: {
    submissionId: string
    taskRef: string
    kind: 'ask' | 'explain'
    question?: string
  },
) {
  const ai = await getAiStatus()
  if (!ai.configured) throw new RequestRejected('ai_not_configured')
  const { ctx } = context.scope
  const row = await visible(
    withTransactionalSql(getDb().pool, (tx, queue) =>
      createQuestion(withOrg(tx, ctx), {
        submissionId: input.submissionId,
        kind: input.kind,
        taskRef: input.taskRef,
        ...(input.question === undefined ? {} : { question: input.question }),
        budgetDefaults: getBudgetDefaults(),
        enqueue: (payload) => enqueueQuestion(payload, queue),
      }),
    ),
  )
  return toQuestionView(row)
}

export const askAiFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg, limitExpensive])
  .validator(askAiSchema)
  .handler(({ context, data }) => queueQuestion(context, { ...data, kind: 'ask' }))

export const explainTaskFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg, limitExpensive])
  .validator(explainTaskSchema)
  .handler(({ context, data }) => queueQuestion(context, { ...data, kind: 'explain' }))

/** The grade list of one exam. Only for someone who may see the exam, like the grading page. */
export const getGradeListFn = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) =>
    toGradeListView(await visible(getGradingOverview(context.scope, data.examId))),
  )

export const getPrintViewFn = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) =>
    toPrintView(await visible(getGradingOverview(context.scope, data.examId))),
  )
